/**
 * ThreeGame — React component that mounts a Three.js WebGLRenderer.
 *
 * Parallel to PhaserGame.js — same props, same EventBus contract, same
 * lifecycle (scene-ready handshake, prop-driven updates, cleanup).
 *
 * Orientation: the wrapper aspect ratio + EventBus 'set-orientation'
 * are driven by the resolved viewport orientation (coach-app defaults
 * to portrait playbook view, learner-app to landscape broadcast view —
 * see ViewportOrientationContext). When 'portrait' the wrapper is
 * 53.3:100 (tall) and FootballScene3D rotates the top-down camera so
 * world +x (downfield) maps to screen-up.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import EventBus from './EventBus';
import RendererRegistry from './RendererRegistry';
import { useViewportOrientation, resolveOrientation } from '../context/ViewportOrientationContext';

const MANAGED_EVENTS = [
  'set-manifest', 'set-actors', 'update-frame', 'switch-camera',
  'scene-ready', 'start-clock', 'stop-clock', 'set-annotations',
  'camera-changed', 'set-orientation',
];

const ThreeGame = ({ manifest, actors, frame, sceneKey = 'football_3d', orientationOverride }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const rafRef = useRef(null);
  const manifestRef = useRef(manifest);
  const actorsRef = useRef(actors);
  const frameRef = useRef(frame);
  const readyRef = useRef(false);

  // Resolve the orientation. `orientationOverride` is passed by the
  // Scene Designer's "view as learner" toggle to flip the editor
  // preview without touching the user's persisted preference; other
  // mounts inherit the active context value.
  const orientationCtx = useViewportOrientation();
  const orientation = resolveOrientation(orientationOverride, orientationCtx);
  const orientationRef = useRef(orientation);
  orientationRef.current = orientation;

  manifestRef.current = manifest;
  actorsRef.current = actors;
  frameRef.current = frame;

  const pushCurrentData = useCallback(() => {
    // Push orientation FIRST so the cameraCtrl picks it up before the
    // first manifest-driven applyManifest call constructs the camera.
    EventBus.emit('set-orientation', orientationRef.current);
    if (manifestRef.current) EventBus.emit('set-manifest', manifestRef.current);
    if (actorsRef.current && actorsRef.current.length > 0) EventBus.emit('set-actors', actorsRef.current);
    if (frameRef.current) EventBus.emit('update-frame', frameRef.current);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    // Coaches reported "the top down view is cutting off the bottom
    // WRs" in the editor — the editor sidebar was squeezing the
    // canvas to a much narrower aspect than the 900x480 we hardcoded
    // here, but the renderer never knew. Initial size now derives
    // from the live wrapper bounds, and a ResizeObserver keeps both
    // the WebGL renderer AND the orthographic / perspective camera
    // frustums in sync as the React layout reshapes the canvas.
    const initialWidth = Math.max(1, Math.round(container.clientWidth || 900));
    const initialHeight = Math.max(1, Math.round(container.clientHeight || 480));

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(initialWidth, initialHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const SceneClass = RendererRegistry.getScene(sceneKey);
    const scene3d = new SceneClass(renderer);
    sceneRef.current = scene3d;

    readyRef.current = false;

    const onSceneReady = () => {
      readyRef.current = true;
      pushCurrentData();
    };
    EventBus.on('scene-ready', onSceneReady);

    scene3d.init();

    // Keep WebGL canvas + camera frustums in sync with the wrapper
    // <div>. The editor sidebar resize, the right-hand panel toggle,
    // and the 2D⇄3D switcher all reshape this container without
    // firing a window-level resize. Without an observer here, the
    // top-down ortho frustum stays bound to whatever aspect the
    // initial layout had and clips off-frame players when the layout
    // changes.
    const applyResize = () => {
      const w = Math.max(1, Math.round(container.clientWidth));
      const h = Math.max(1, Math.round(container.clientHeight));
      try {
        renderer.setSize(w, h, false);
        if (sceneRef.current && typeof sceneRef.current.resize === 'function') {
          sceneRef.current.resize(w, h);
        }
      } catch (_) { /* renderer already disposed */ }
    };
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => applyResize());
      resizeObserver.observe(container);
    }
    window.addEventListener('resize', applyResize);
    // First explicit pass — covers the case where the container
    // resized between the initial measure above and the observer's
    // first callback (notably in the coach editor where the sidebar
    // mounts after RenderRouter's first paint).
    applyResize();

    let running = true;
    const animate = (now) => {
      if (!running) return;
      scene3d.update(now);
      scene3d.render();
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      EventBus.off('scene-ready', onSceneReady);
      MANAGED_EVENTS.forEach((e) => EventBus.removeAll(e));
      window.removeEventListener('resize', applyResize);
      if (resizeObserver) {
        try { resizeObserver.disconnect(); } catch (_) { /* noop */ }
      }
      scene3d.dispose();
      renderer.dispose();
      if (container && renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
      rendererRef.current = null;
      sceneRef.current = null;
      readyRef.current = false;
    };
  }, [sceneKey, pushCurrentData]);

  useEffect(() => {
    if (manifest && readyRef.current) EventBus.emit('set-manifest', manifest);
  }, [manifest]);

  useEffect(() => {
    if (actors && actors.length > 0 && readyRef.current) EventBus.emit('set-actors', actors);
  }, [actors]);

  useEffect(() => {
    if (frame && readyRef.current) EventBus.emit('update-frame', frame);
  }, [frame]);

  useEffect(() => {
    // Re-push orientation whenever it changes (user toggle, "view as
    // learner" preview flip, multi-tab sync). The ResizeObserver above
    // independently rebuilds the ortho frustum to the new container
    // aspect ratio, so we don't have to call resize() here ourselves.
    if (readyRef.current) EventBus.emit('set-orientation', orientation);
  }, [orientation]);

  // Sizing strategy:
  //   - SessionPage mounts ThreeGame inside `.renderer-container` with
  //     no explicit height; the wrapper here therefore needs an
  //     intrinsic height so the canvas doesn't collapse to 0 px tall.
  //     We set aspectRatio based on orientation so the wrapper grows
  //     by width — '100 / 53.3' for landscape (broadcast), '53.3 / 100'
  //     for portrait (playbook).
  //   - SceneDesignerPage's editor and preview wrappers each set their
  //     own aspectRatio in step with the active orientation; setting
  //     it again here is a no-op when the parent already constrains.
  //   - The ResizeObserver in the effect above reads
  //     container.clientWidth / clientHeight on every layout change
  //     and rebuilds the WebGL viewport + ortho frustum to match —
  //     so the field stays correctly framed even when the React
  //     sidebar resizes the editor canvas.
  const wrapperAspect = orientation === 'portrait' ? '53.3 / 100' : '100 / 53.3';
  return (
    <div
      ref={containerRef}
      className="three-game"
      data-testid="three-game-container"
      data-orientation={orientation}
      style={{
        width: '100%',
        aspectRatio: wrapperAspect,
        position: 'relative',
        margin: '0 auto',
      }}
    />
  );
};

export default ThreeGame;
